// Shared types between the React app and the Cloudflare Worker.

export type Genre = "poetry" | "prose";

export interface Confirms {
  c1: boolean; c2: boolean; c3: boolean;
  p1: boolean; p2: boolean;
  pr1: boolean; pr2: boolean; pr3: boolean;
}

export interface SubmissionPayload {
  first: string;
  last: string;
  displayName: string;
  email: string;
  genre: Genre | "";
  title1: string;
  body1: string;
  title2: string;
  body2: string;
  bio: string;
  confirms: Confirms;
  artworkInterest: boolean;
  /** Honeypot — must be empty for the request to count as legitimate. */
  website: string;
  /** Cloudflare Turnstile token from the widget callback. */
  turnstileToken: string;
}

export interface SubmissionSuccess {
  ok: true;
  taskId: string;
  emailStatus: EmailStatus;
}

export type EmailStatus = "sent" | "skipped" | "failed";

export interface SubmissionFailure {
  ok: false;
  errors: string[];
}

export type SubmissionResponse = SubmissionSuccess | SubmissionFailure;

// ── Help / change-request payload ─────────────────────────────────────────
// Submitted from the same form site via a separate welcome-modal path. Goes
// to the same ClickUp list but with a status indicating what the writer
// needs (format the piece for them, change something, or withdraw).
export type HelpRequestType = "format" | "change" | "withdraw";

export interface HelpPayload {
  first: string;
  last: string;
  email: string;
  displayName: string;
  requestType: HelpRequestType;
  pieceTitle: string;
  message: string;
  /** Honeypot — must be empty. */
  website: string;
  /** Cloudflare Turnstile token. */
  turnstileToken: string;
}

export type HelpResponse = SubmissionResponse;

// ── Proof round ───────────────────────────────────────────────────────────
// A writer opens their typeset proof and reports whether we set it correctly.
// This is NOT a revision round — see the copy in the UI. Two outcomes only:
// "ok" (runs as printed) and "fix" (we made a typesetting error).

export type ProofVerdict = "ok" | "fix";
export const PROOF_TURNSTILE_ACTION = "proof_lookup";

/** Sent to /api/proof/lookup. Supply exactly one of `email` or `token`. */
export interface ProofLookupPayload {
  email?: string;
  /** Opaque token from the ?k= parameter in the writer's proof email. */
  token?: string;
  /** Honeypot — must be empty. */
  website: string;
  /** Cloudflare Turnstile token. Not required when `token` is supplied. */
  turnstileToken: string;
}

export interface ProofRecord {
  /** Signed token; also the credential for /api/proof/file and /respond. */
  token: string;
  first: string;
  name: string;
  titles: string[];
  /** Same-origin URL that streams the PDF out of R2. */
  fileUrl: string;
  /** Already answered once — the UI shows their previous verdict. */
  answered: ProofVerdict | null;
  /** The PDF remains viewable after closure, but responses are disabled. */
  closed: boolean;
  deadlineLabel: string;
  /** Public issue name, e.g. "Method Writers Speak #11". */
  issueName: string;
  /** MWS Desk published this writer's text, so they may edit it within the page limit. */
  editable: boolean;
  /** Their contributor bio: whether we still need one, what we have, and what they've sent. */
  bio: ProofBioState;
}

// ── Contributor bios ──────────────────────────────────────────────────────
export const BIO_WORDS = { min: 7, max: 250 };

export interface ProofBioState {
  /** No usable bio yet: approving is blocked until they send one. */
  needed: boolean;
  /** The bio we have now (may need work), "" if none. */
  current: string;
  /** What they sent through the site, newest; null if nothing yet. */
  sent: string | null;
}

/** POST /api/proof/bio-lookup — the bio-only page (?b=<token>). */
export interface BioLookupSuccess {
  ok: true;
  first: string;
  name: string;
  issueName: string;
  deadlineLabel: string;
  bio: ProofBioState;
}
export type BioLookupResponse = BioLookupSuccess | ProofLookupFailure;

/** POST /api/proof/bio — send from the proof page (token) or the bio page (bioToken). */
export interface BioSendPayload {
  token?: string;
  bioToken?: string;
  bio: string;
}
export type BioSendResponse = { ok: true; bio: string } | ProofLookupFailure;

/** Stored in R2 under <issue>/bios/, collected by MWS Desk. */
export interface ProofBioRecord {
  email: string;
  name: string;
  taskId: string;
  at: string;
  bio: string;
}

export interface ProofLookupResolved {
  ok: true;
  kind: "proof";
  proof: ProofRecord;
}

export interface ProofLinkRequested {
  ok: true;
  kind: "link-requested";
}

/**
 * `reason: "hold"` means we know this writer but their piece has an unresolved
 * correction, so there is no proof to show yet. Distinct from "notfound" so the
 * UI can say something true rather than implying they aren't in the issue.
 */
export interface ProofLookupFailure {
  ok: false;
  reason: "notfound" | "hold" | "closed" | "invalid" | "bio";
  errors: string[];
}

export type ProofLookupResponse = ProofLookupResolved | ProofLinkRequested | ProofLookupFailure;

export interface ProofRespondPayload {
  token: string;
  verdict: ProofVerdict;
  /** Identifies one deliberate answer and is reused only when that answer is retried. */
  attemptId: string;
  /** What we got wrong. Required when verdict is "fix". */
  note: string;
}

export interface ProofRespondSuccess {
  ok: true;
  verdict: ProofVerdict;
  emailStatus: EmailStatus;
  /** The same response attempt was already recorded, so no new receipt was sent. */
  duplicate?: boolean;
}

export type ProofRespondResponse = ProofRespondSuccess | ProofLookupFailure;

// ── Proof round: writer editing ───────────────────────────────────────────
// When MWS Desk publishes a writer's text next to their PDF, the proof page
// offers "Make changes". The writer edits their own text; the only rule is the
// page limit. Text uses the same plain format as MWS Desk: a blank line starts a
// new stanza (or paragraph), a newline is a line break, <i>…</i> is italics.

export interface ProofTextPiece {
  /** MWS Desk's id for the piece, echoed back so edits land on the right piece. */
  pieceId: string;
  title: string;
  genre: "poetry" | "prose";
  text: string;
  /** Footnotes. Each one's mark sits in `text` as [^n]; the writer can correct a note's text but not add or remove notes. */
  notes?: ProofNoteText[];
}

/** A footnote: its number (the [^n] mark in the text) and its text (the proof format: <i>/<b> allowed). */
export interface ProofNoteText {
  n: number;
  text: string;
}

/** A footnote mark in a piece's text. */
export const NOTE_MARK = /\[\^(\d{1,3})\]/g;
export const MAX_NOTE_TEXT = 2_000;

/** The note numbers whose marks are in a text, in order of appearance (each once). */
export function noteMarks(text: string): number[] {
  const out: number[] = [];
  for (const m of text.matchAll(NOTE_MARK)) {
    const n = Number(m[1]);
    if (!out.includes(n)) out.push(n);
  }
  return out;
}

/** Notes held to the proof allow-list: only the numbers allowed, each once, non-empty, within the length limit. */
export function cleanProofNotes(raw: unknown, allowed: Set<number>): ProofNoteText[] {
  if (!Array.isArray(raw)) return [];
  const out: ProofNoteText[] = [];
  for (const x of raw.slice(0, 200)) {
    if (!x || typeof x !== "object") continue;
    const n = Number((x as { n?: unknown }).n);
    const t = (x as { text?: unknown }).text;
    if (!Number.isInteger(n) || !allowed.has(n) || out.some((o) => o.n === n) || typeof t !== "string" || t.length > MAX_NOTE_TEXT * 2) continue;
    const text = sanitizeProofText(t).replace(/\n+/g, " ");
    if (text && text.length <= MAX_NOTE_TEXT) out.push({ n, text });
  }
  return out.sort((a, b) => a.n - b.n);
}

/** The writer's text as published by MWS Desk (R2: <issue>/<text key>). */
export interface ProofText {
  pieces: ProofTextPiece[];
}

export interface ProofTextResponse {
  ok: true;
  text: ProofText;
  limits: ProofLimitRules;
}

export interface ProofEditPayload {
  token: string;
  /** Identifies one deliberate send; reused only when that send is retried. */
  attemptId: string;
  pieces: Array<{ pieceId: string; title: string; text: string; notes?: ProofNoteText[] }>;
  /** Optional message to the editor. */
  note: string;
}

export interface ProofEditSuccess {
  ok: true;
  /** Canonical content persisted by the server, including duplicate retries. */
  saved: Pick<ProofEditRecord, "pieces" | "note">;
  emailStatus: EmailStatus;
  duplicate?: boolean;
}

export type ProofEditResponse = ProofEditSuccess | ProofLookupFailure;

/** One stored edit, as MWS Desk reads it back from /api/proof/edits. */
export interface ProofEditRecord {
  id: string;
  taskId: string;
  email: string;
  name: string;
  at: string;
  pieces: Array<{ pieceId: string; title: string; text: string; notes?: ProofNoteText[] }>;
  note: string;
}

// ── The page limit (one counter for the browser and the Worker) ──────────
export interface ProofLimitRules {
  /** Poetry: lines across all of a writer's poems, counting blank lines between stanzas. */
  poetryLines: number;
  /** Prose: words per piece. */
  proseWords: number;
}

export const PROOF_LIMITS: ProofLimitRules = { poetryLines: 70, proseWords: 500 };

export interface ProofLimitReport {
  genre: "poetry" | "prose";
  /** Poetry: total lines used. Prose: words in the longest piece. */
  used: number;
  max: number;
  over: boolean;
  perPiece: Array<{ pieceId: string; used: number }>;
}

function stripTags(s: string): string {
  return s.replace(/<\/?[ib]>/gi, "").replace(NOTE_MARK, "");          // a footnote mark isn't a word
}

/** Lines a poem uses toward the cap: its lines plus the blank lines between stanzas. */
export function poemLines(text: string): number {
  const stanzas = text.replace(/\r/g, "").trim().split(/\n[ \t]*\n+/).filter((s) => s.trim());
  const lines = stanzas.reduce((n, s) => n + s.split("\n").filter((l) => stripTags(l).trim()).length, 0);
  return lines + Math.max(0, stanzas.length - 1);
}

export function proseWords(text: string): number {
  const t = stripTags(text).trim();
  return t ? t.split(/\s+/).length : 0;
}

export function checkProofLimits(
  pieces: Array<{ pieceId: string; genre: "poetry" | "prose"; text: string }>,
  rules: ProofLimitRules = PROOF_LIMITS,
): ProofLimitReport {
  const poetry = pieces.some((p) => p.genre === "poetry");
  if (poetry) {
    const perPiece = pieces.map((p) => ({ pieceId: p.pieceId, used: poemLines(p.text) }));
    const used = perPiece.reduce((n, p) => n + p.used, 0);
    return { genre: "poetry", used, max: rules.poetryLines, over: used > rules.poetryLines, perPiece };
  }
  const perPiece = pieces.map((p) => ({ pieceId: p.pieceId, used: proseWords(p.text) }));
  const used = Math.max(0, ...perPiece.map((p) => p.used));
  return { genre: "prose", used, max: rules.proseWords, over: used > rules.proseWords, perPiece };
}

// ── Proof round: sanitizing writers' text ─────────────────────────────────────
// This is a plain-text format, NOT safe HTML. Renderers must still escape it.
// A forward-only scanner recognizes markup once; removed markup can never be
// reinterpreted. Literal '<' beside tag-like text is separated with a space.
// Formatting survives line breaks and is emitted in a deterministic order,
// balanced on each line. No DOM or backtracking tag regex is needed.
const PROOF_CONTROL = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F\u200E\u200F\u202A-\u202E\u2066-\u2069\uFEFF]/g;
type ProofRun = { text: string; italic: boolean; bold: boolean };

function canonicalProof(input: unknown, formatting: boolean): string {
  if (typeof input !== "string") return "";
  const source = input.normalize("NFC").replace(/\r\n?/g, "\n")
    .replace(/\u00a0|\t/g, " ").replace(PROOF_CONTROL, "");
  let lines: ProofRun[][] = [[]];
  let italic = 0;
  let bold = 0;
  const append = (text: string) => {
    const parts = text.split("\n");
    for (let n = 0; n < parts.length; n++) {
      if (n) lines.push([]);
      if (parts[n]) {
        const line = lines[lines.length - 1];
        const i = formatting && italic > 0;
        const b = formatting && bold > 0;
        const last = line[line.length - 1];
        if (last && last.italic === i && last.bold === b) last.text += parts[n];
        else line.push({ text: parts[n], italic: i, bold: b });
      }
    }
  };
  let start = 0;
  while (start < source.length) {
    const at = source.indexOf("<", start);
    if (at < 0) { append(source.slice(start)); break; }
    append(source.slice(start, at));
    // Comparisons and lone brackets are writing, not markup.
    if (!/[A-Za-z/!?]/.test(source[at + 1] ?? "")) {
      append("<");
      start = at + 1;
      continue;
    }
    if (source.startsWith("<!--", at)) {
      const end = source.indexOf("-->", at + 4);
      start = end < 0 ? source.length : end + 3;
      continue;
    }
    let end = at + 1;
    let quote = "";
    // Advance once through even malformed or unterminated markup. Nested '<'
    // stays inside this token rather than creating new syntax on the next pass.
    for (; end < source.length; end++) {
      const c = source[end];
      if (quote) { if (c === quote) quote = ""; }
      else if (c === '"' || c === "'") quote = c;
      else if (c === ">") break;
    }
    if (end === source.length) {
      // An unfinished ordinary tag may just be writing ("x<y"). Preserve it
      // as inert text instead of silently deleting the rest of the piece.
      if (!/[!?]/.test(source[at + 1])) append(source.slice(at));
      break;
    }
    if (end < source.length) {
      const marker = /^<(\/?)(i|b|em|strong)\s*>$/i.exec(source.slice(at, end + 1));
      if (marker) {
        const delta = marker[1] ? -1 : 1;
        if (/^(i|em)$/i.test(marker[2])) italic = Math.max(0, italic + delta);
        else bold = Math.max(0, bold + delta);
      }
    }
    start = end + 1;
  }

  // Trim writing before emitting markers; otherwise whitespace-only formatting
  // can hide empty content or grow on repeated saves.
  for (const line of lines) {
    while (line.length) {
      const last = line[line.length - 1];
      last.text = last.text.trimEnd();
      if (last.text) break;
      line.pop();
    }
  }
  let first = 0;
  while (first < lines.length && !lines[first].length) first++;
  lines = lines.slice(first);
  while (lines.length && !lines[lines.length - 1].length) lines.pop();
  if (lines[0]) {
    let firstRun = 0;
    while (firstRun < lines[0].length) {
      lines[0][firstRun].text = lines[0][firstRun].text.trimStart();
      if (lines[0][firstRun].text) break;
      firstRun++;
    }
    lines[0] = lines[0].slice(firstRun);
  }
  const rendered = lines.map((line) => {
    const out: string[] = [];
    let active: string[] = [];
    for (const run of line) {
      const next = [...(run.italic ? ["i"] : []), ...(run.bold ? ["b"] : [])];
      let common = 0;
      while (common < active.length && active[common] === next[common]) common++;
      for (let n = active.length - 1; n >= common; n--) out.push(`</${active[n]}>`);
      for (let n = common; n < next.length; n++) out.push(`<${next[n]}>`);
      // Protect literal brackets at run boundaries too, including boundaries
      // left by removed tags. Keep comparisons such as "a < b" intact.
      out.push(run.text.replace(/<(?=[A-Za-z/!?<]|$)/g, "< "));
      active = next;
    }
    for (let n = active.length - 1; n >= 0; n--) out.push(`</${active[n]}>`);
    return out.join("").trimEnd();
  });
  // Deletion can bring combining characters together: normalize the result too.
  return rendered.join("\n").replace(/\n{3,}/g, "\n\n").normalize("NFC");
}

export function sanitizeProofText(input: unknown): string {
  return canonicalProof(input, true);
}

/** Titles and notes have no markers; sanitize directly, never strip generated tags. */
export function sanitizeProofPlain(input: unknown, oneLine = false): string {
  const text = canonicalProof(input, false);
  return oneLine ? text.replace(/\s+/g, " ").trim() : text;
}

// ── Artwork details (?art=<token>) ────────────────────────────────────────
export interface ArtworkItem {
  /** The file's name on the writer's computer, as they'll upload it. */
  file: string;
  title: string;
  untitled: boolean;
  medium: string;
  year: string;
}
export type ArtworkLookupResponse =
  | { ok: true; name: string; first: string; items: ArtworkItem[]; savedAt: string; uploadUrl: string }
  | { ok: false; reason: "notfound" | "invalid"; errors: string[] };
export type ArtworkSaveResponse =
  | { ok: true; items: ArtworkItem[]; savedAt: string }
  | { ok: false; reason: "notfound" | "invalid"; errors: string[] };

// ── The editor's final proof (?final=): what the editor can change ──────────
// MWS Desk publishes one item per thing the editor may edit: a writer's pieces, a writer's bio, the opening quote
// (with its note), and the title/copyright page lines as fields. His changes go back to MWS Desk as suggestions:
// nothing on the site changes the book, and nothing is sent to the writer.
export type FinalItemKind = "writer" | "bio" | "quote" | "lines";

export interface FinalField {
  key: string;
  label: string;
  value: string;
  /** Several lines allowed (a list of books); otherwise one line. */
  multiline?: boolean;
}

export interface FinalItemSummary {
  id: string;
  kind: FinalItemKind;
  /** e.g. the writer's name, "Opening quote". */
  label: string;
  /** e.g. their titles. */
  sub?: string;
}

export interface FinalItem extends FinalItemSummary {
  pieces?: ProofTextPiece[];
  fields?: FinalField[];
}

/** Same shape as ProofTextResponse, so the proof editor can open an item as it opens a writer's text. */
export interface FinalTextResponse {
  ok: true;
  item: FinalItem;
  text: ProofText;
  limits: ProofLimitRules;
}

export interface FinalEditRecord {
  id: string;
  /** The item's id, or "note" for a general note. */
  item: string;
  kind: FinalItemKind | "note";
  label: string;
  at: string;
  pieces?: Array<{ pieceId: string; title: string; text: string; notes?: ProofNoteText[] }>;
  fields?: Array<{ key: string; value: string }>;
  note: string;
}
